import {
    type Department,
    type RosterTargets,
    type TimeEntry,
    TimeEntryType,
    getAutomaticBreak,
    getDateArray,
    toDateString,
    toDurationString,
} from "@pentacode/core";
import { type DateString, type Hours, add, subtract } from "@pentacode/openapi";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { app, router } from "../../../init";
import { hasPageAccess } from "../../app-routes";
import { routeAccess } from "../../route-access";
import { type ColWindow, renderDayWindow } from "../lib/day-window";

@customElement("ptc-roster-targets")
export class RosterTargetsElement extends LitElement {
    @property({ attribute: false })
    targets: RosterTargets[] = [];

    @property({ attribute: false })
    department: Department;

    @property({ attribute: false })
    entries: TimeEntry[] = [];

    @property({ type: Boolean })
    editable: boolean;

    @property({ attribute: false })
    from: DateString;

    @property({ attribute: false })
    to: DateString;

    @property({ attribute: false })
    colWindow: ColWindow | null = null;

    @property({ type: Number })
    colWidth = 0;

    createRenderRoot() {
        return this;
    }

    private _getActualWorkDurationForDate(date: DateString) {
        return this.entries
            .filter(
                (e) =>
                    !e.deleted &&
                    e.type === TimeEntryType.Work &&
                    e.date === date &&
                    e.position &&
                    e.position.departmentId === this.department.id
            )
            .reduce((acc, entry) => {
                const entryDuration = subtract(
                    entry.durationPlanned,
                    getAutomaticBreak(entry.durationPlanned, app.getTimeSettings({ timeEntry: entry }))
                );
                return add(acc, entryDuration);
            }, 0 as Hours);
    }

    static styles = css`
        .department-targets {
            position: sticky;
            bottom: 0;
            z-index: 20;
            background: rgba(255, 255, 255, 0.95);
            border-top: solid 1px var(--shade-1);
        }

        .department-targets-header {
            position: sticky;
            background: var(--color-bg);
            z-index: 7;
            left: 0;
        }

        .department-targets button {
            padding: 0.3em 0.5em;
            margin: 0.4em;
        }

        .department-targets-day {
            padding: 0.3em 0;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .department-day-quota {
            background: #fafafa;
            border-radius: var(--border-radius);
            overflow: hidden;
            position: relative;
            text-align: center;
            font-size: 0.85em;
            width: 100px;
            height: 24px;
            line-height: 24px;
            font-weight: bold;
            color: var(--color-highlight);
            border: solid 2px;
        }

        .department-day-numbers {
            width: 100px;
            box-sizing: border-box;
        }

        .department-day-progress {
            overflow: hidden;
            position: absolute;
            top: 0;
            left: 0;
        }

        .department-day-progress-bar {
            background: var(--color-highlight);
            color: var(--color-bg);
            text-shadow: rgba(0, 0, 0, 0.2) 0 1px 1px;
        }
    `;

    render() {
        if (!this.department) {
            return;
        }

        if (!this.targets.some((t) => t.departmentId === this.department.id)) {
            return this.editable && hasPageAccess("planning", routeAccess())
                ? html`
                      <div class="row department-targets">
                          <div class="department-targets-header horizontal center-justifying layout">
                              <button class="small subtle" @click=${() => router.go("planning/roster")}>
                                  <ptc-ui-icon name="icon.planning.staffing-target"></ptc-ui-icon>
                                  Stundenvorgaben
                              </button>
                          </div>
                      </div>
                  `
                : "";
        }

        const today = toDateString(new Date());
        const dates = getDateArray(this.from, this.to);

        return html`
            <div class="row department-targets">
                <div class="department-targets-header horizontal center-justifying layout">
                    <button
                        class="small subtle"
                        @click=${() => router.go("planning/roster")}
                        ?hidden=${!this.editable || !hasPageAccess("planning", routeAccess())}
                    >
                        <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon>
                        Stundenvorgaben
                    </button>
                </div>
                <div class="balance-before" ?hidden=${!app.settings.rosterDisplayTimeBalances}>
                    <div><ptc-ui-icon name="icon.time-entry.adjustment"></ptc-ui-icon></div>
                </div>

                ${renderDayWindow(dates, this.colWindow, this.colWidth, (date) => {
                    const targets = this.targets.find((t) => t.departmentId === this.department.id && t.date === date);

                    if (!targets || !targets.hours) {
                        return html` <div class="department-targets-day"></div> `;
                    }

                    const { hours: nominal } = targets;
                    const actual = this._getActualWorkDurationForDate(date);
                    const diff = (actual - nominal) / nominal;

                    return html`
                        <div class="department-targets-day ${date === today ? "today" : ""}">
                            ${targets
                                ? html`
                                      <div
                                          class="department-day-quota ${diff > 0 ? "red" : ""}"
                                          ?disabled=${date < today}
                                      >
                                          <div
                                              class="department-day-progress"
                                              style="width: ${Math.min(1, actual / nominal) * 100}%"
                                          >
                                              <div class="department-day-numbers department-day-progress-bar">
                                                  ${toDurationString(actual, true)} / ${toDurationString(nominal, true)}
                                              </div>
                                          </div>

                                          <div class="department-day-numbers">
                                              ${toDurationString(actual, true)} / ${toDurationString(nominal, true)}
                                          </div>
                                      </div>
                                  `
                                : ""}
                        </div>
                    `;
                })}
                <div class="balance-after" ?hidden=${!app.settings.rosterDisplayTimeBalances}>
                    <div><ptc-ui-icon name="icon.time-entry.adjustment"></ptc-ui-icon></div>
                </div>
            </div>
        `;
    }
}
